import { getStateData } from "analytics/index";
import {
  CLICKSTREAM_EVENT_NAMES,
  flattenData
} from "constants/AnalyticsTypeConstants";
import { get, map } from "lodash";
import {
  CONTRACT_ITEM_TYPES,
  CONTRACT_TYPES,
  MANAGE_ORDER_URL,
  paths,
  PAYMENT_STATUSES_CXOS,
  PLAN_TYPE,
  ROOT_CHANNEL,
  SIGNINTYPE,
  SIM_TYPES,
  WEB_CHANNEL
} from "utils/constants";
import { lower, removeSensitiveData } from "utils/helpers";
import { getItem, setItem } from "utils/localStorage";

export const constructClickStreamEventInfo = (event, data, reduxStore) => {
  let clickstreamEventProperties = {};
  switch (event) {
    case "page_visit":
      {
        let clickstreamEventName = "";
        let clickstreamProperties = {};
        const currentPath = window.location.pathname;
        const currentUrl = window.location.origin + currentPath;
        if (
          currentPath ===
          get(paths, "PRE_CHECKOUT").replace(ROOT_CHANNEL, WEB_CHANNEL)
        ) {
          clickstreamEventName = CLICKSTREAM_EVENT_NAMES.PURCHASE_FLOW_STARTED;
        }
        // Event trigger for when user completes order (lands on payment-success page)
        if (
          currentPath ===
          get(paths, "PAYMENT_SUCCESS", "").replace(ROOT_CHANNEL, WEB_CHANNEL)
        ) {
          const customer_id = getStateData(
            reduxStore,
            "api.loggedInUserDetails.data.external_id",
            ""
          );
          const order_id = getStateData(
            reduxStore,
            "api.order.data.orderReference",
            ""
          );
          clickstreamEventName = CLICKSTREAM_EVENT_NAMES.ORDER_CONFIRMATION;
          clickstreamProperties = {
            customer_id,
            order_id
          };
        }

        if (currentPath === get(paths, "MANAGE_MANUAL_EKYC", "")) {
          clickstreamEventName = CLICKSTREAM_EVENT_NAMES.KYC_STARTED;
        }
        if (currentPath === get(paths, "MANAGE_SIM_ACTIVATION")) {
          let numberType = lower(
            getStateData(reduxStore, "api.guestOrder.data.contract_type")
          );

          if (numberType === lower(get(CONTRACT_TYPES, "NEW")))
            numberType = lower(get(CONTRACT_ITEM_TYPES, "NEW_NUMBER"));
          clickstreamEventProperties = {
            name: CLICKSTREAM_EVENT_NAMES.ACTIVATION_INITIATION,
            properties: {
              number_type: numberType
            }
          };
        }
        if (
          currentPath.includes(
            get(paths, "RESCHEDULE_DELIVERY", "").replace(":order_type?", "")
          )
        ) {
          clickstreamEventName = CLICKSTREAM_EVENT_NAMES.TAP;
          clickstreamProperties = {
            item_name: "Reschedule Delivery",
            item_location: currentUrl
          };
        }
        clickstreamEventProperties = {
          name: clickstreamEventName,
          properties: {
            ...clickstreamProperties
          }
        };
      }
      break;
    case "eligiblePlans_api_call_requested":
      clickstreamEventProperties = {
        name: CLICKSTREAM_EVENT_NAMES.CATEGORY_SELECTED,
        properties: {
          plan_type: get(data, "productCategory")
        }
      };
      break;
    case "postpaid_planService_requested":
      clickstreamEventProperties = {
        name: CLICKSTREAM_EVENT_NAMES.PLAN_SELECTED,
        properties: {
          plan_type: get(data, "plan_name")
        }
      };
      break;
    case "addon_selected":
      // Caters for prepaid/postpaid addon selection
      clickstreamEventProperties = {
        name:
          get(data, "productCategory", "") +
          "_" +
          CLICKSTREAM_EVENT_NAMES.ADDON_SELECTED,
        properties: {
          addon_name: get(data, "addon.title", "")
        }
      };
      break;
    case "addon_deselected":
      {
        // Caters for prepaid/postpaid addon deselection and deletion
        const currentUrl = window.location.pathname;
        let clickstreamEventName =
          get(data, "productCategory", "") +
          "_" +
          CLICKSTREAM_EVENT_NAMES.ADDON_DESELECTED;
        if (
          currentUrl ===
          get(paths, "SELECTED_PACKAGES", "").replace(ROOT_CHANNEL, WEB_CHANNEL)
        )
          clickstreamEventName = CLICKSTREAM_EVENT_NAMES.PREPAID_ADDON_DELETED;

        clickstreamEventProperties = {
          name: clickstreamEventName,
          properties: {
            addon_name: get(data, "addon.title", "")
          }
        };
      }
      break;
    case "PreCheckoutForm_submitted":
      {
        const previousUrl = window.location.pathTracking.previous;
        const { plan_name, userSelectedAddons, productCategory } = data;
        let clickstreamEventName = "";
        let clickstreamProperties = {};
        const selectedPackagesPage = get(
          paths,
          "SELECTED_PACKAGES",
          ""
        ).replace(ROOT_CHANNEL, WEB_CHANNEL);
        const addonSelectionPage = get(paths, "ADDON_SELECTION", "").replace(
          ROOT_CHANNEL,
          WEB_CHANNEL
        );

        if (
          previousUrl === selectedPackagesPage ||
          previousUrl === addonSelectionPage
        ) {
          clickstreamEventName =
            productCategory === get(PLAN_TYPE, "PREPAID")
              ? CLICKSTREAM_EVENT_NAMES.PREPAID_PACKAGE_SELECTED
              : CLICKSTREAM_EVENT_NAMES.PLAN_SELECTED_2;
          clickstreamProperties = {
            ...(productCategory === get(PLAN_TYPE, "PREPAID") && {
              prepaid_addon_selected: map(
                userSelectedAddons,
                userSelectedAddon => get(userSelectedAddon, "title"),
                []
              ).join(", ")
            }),
            ...(productCategory === get(PLAN_TYPE, "POSTPAID") && {
              plan_type: plan_name,
              addon_selected: map(
                userSelectedAddons,
                userSelectedAddon => get(userSelectedAddon, "title"),
                []
              ).join(", ")
            })
          };
        }
        clickstreamEventProperties = {
          name: clickstreamEventName,
          properties: {
            ...clickstreamProperties
          }
        };
      }
      break;
    case "userLoginOTP_api_call_succeeded":
      clickstreamEventProperties = {
        name: CLICKSTREAM_EVENT_NAMES.SUBMIT_EMAIL,
        properties: {}
      };
      break;
    case "userAuthenticate_api_call_succeeded":
      {
        const currentUrl = window.location.pathname;
        clickstreamEventProperties = {
          name: currentUrl.includes(`${get(SIGNINTYPE, "SIGNUP", "")}`)
            ? CLICKSTREAM_EVENT_NAMES.VERIFICATION_SUBMITTED
            : "",
          properties: {}
        };
      }
      break;
    case "sim_type_selected":
      {
        let simType = lower(
          get(
            data,
            "formValues.sim_type.value",
            get(data, "formValues.sim_type", "")
          )
        );
        if (simType === lower(get(SIM_TYPES, "E_SIM", "")))
          simType = lower(get(SIM_TYPES, "ESIM", ""));

        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.SIM_SELECTED,
          properties: {
            sim_type: simType
          }
        };
      }
      break;
    case "number_type_selected":
      {
        let numberType = lower(get(data, "formValues.number_type"));
        if (numberType === lower(get(CONTRACT_TYPES, "NEW")))
          numberType = lower(get(CONTRACT_ITEM_TYPES, "NEW_NUMBER"));
        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.NUMBER_TYPE_SELECTED,
          properties: {
            number_type: numberType
          }
        };
      }
      break;
    case "telco_selected":
      {
        const { formValues, field } = data;
        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.DONOR_TELCO_SELECTED,
          properties: {
            telco: get(formValues, `${field}.label`)
          }
        };
      }
      break;
    case "selectedNumberType_requested":
      {
        let simType = lower(get(data, "sim_type", ""));
        let numberType = lower(get(data, "number_type", ""));
        if (simType === lower(get(SIM_TYPES, "E_SIM")))
          simType = lower(get(SIM_TYPES, "ESIM"));

        if (numberType === lower(get(CONTRACT_TYPES, "NEW")))
          numberType = lower(get(CONTRACT_ITEM_TYPES, "NEW_NUMBER"));

        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.SIM_NUMBER_TYPE_SElECTED,
          properties: {
            sim_type: simType,
            ...(numberType === lower(get(CONTRACT_ITEM_TYPES, "NEW_NUMBER"))
              ? { number_type: numberType }
              : { telco: get(data, "telco.label", "") })
          }
        };
      }
      break;
    case "PersonalDetailsForm_submitted":
      clickstreamEventProperties = {
        name: CLICKSTREAM_EVENT_NAMES.USER_DETAILS_SUBMITTED,
        properties: {
          ...removeSensitiveData(flattenData(get(data, "formValues", {})))
        }
      };
      break;
    case "referralCode_api_call_succeeded":
      {
        let benefits = get(data, "benefits", []).reduce((acc, current) => {
          if (acc) acc = `,${acc}`;
          return `${current.title}${acc}`;
        }, "");
        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.PROMOCODE_USED,
          properties: {
            promocode: get(data, "referral_code", ""),
            promocode_type: get(data, "referral_code_type", ""),
            promo_campaign: get(data, "title", ""),
            benefits
          }
        };
      }
      break;
    case "numberAndDeliverySlotsAvailability_api_call_succeeded":
      {
        const { plan_name, userSelectedAddons, referral_code } = data;
        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.ORDER_SUMMARY_COMPLETED,
          properties: {
            plan_type: get(data, "planService.productCategory"),
            plan_name,
            addon_selected: map(
              userSelectedAddons,
              userSelectedAddon => get(userSelectedAddon, "title"),
              []
            ).join(", "),
            promocode_used: referral_code
          }
        };
      }
      break;
    case "payment_method_selected":
      clickstreamEventProperties = {
        name: CLICKSTREAM_EVENT_NAMES.PAYMENT_SELECTED,
        properties: {
          payment_type: get(data, "formValues.payment_method.code")
        }
      };
      break;
    case "paymentStatus_api_call_succeeded":
      {
        let { payment_status, message, payment_options } = data;
        payment_status = lower(payment_status);
        const psp_reference = get(payment_options, "psp_reference", "");
        const paymentMade = getItem("psp_reference");
        if (psp_reference) setItem("psp_reference", psp_reference);

        if (
          (payment_status === lower(get(PAYMENT_STATUSES_CXOS, "SUCCESS")) ||
            payment_status === lower(get(PAYMENT_STATUSES_CXOS, "FAILURE"))) &&
          !paymentMade
        ) {
          clickstreamEventProperties = {
            name: CLICKSTREAM_EVENT_NAMES.PAYMENT_DETAILS_SUBMITTED,
            properties: {
              status: payment_status,
              ...(message &&
                payment_status ===
                  lower(get(PAYMENT_STATUSES_CXOS, "FAILURE")) && {
                  error_reason: message
                })
            }
          };
        }
      }
      break;
    case "kyc_api_call_succeeded":
      {
        const currentUrl = window.location.origin + window.location.pathname;
        const reconfirmIdentityNeeded = get(
          data,
          "userOrder.next_step.action",
          []
        ).includes("reconfirm_identity");
        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.TAP,
          properties: {
            item_name: reconfirmIdentityNeeded
              ? "Reconfirm your Identity"
              : "Confirm your identity",
            item_location: currentUrl
          }
        };
      }
      break;
    case "manualEkyc_api_call_succeeded":
      clickstreamEventProperties = {
        name: CLICKSTREAM_EVENT_NAMES.KYC_SUBMITTED,
        properties: {}
      };
      break;
    case "faq_item_clicked":
      {
        const currentPath = window.location.pathname;
        const currentUrl = window.location.origin + currentPath;
        if (
          currentPath ===
            get(paths, "PAYMENT_SUCCESS", "").replace(
              ROOT_CHANNEL,
              WEB_CHANNEL
            ) ||
          currentPath === MANAGE_ORDER_URL
        ) {
          clickstreamEventProperties = {
            name: CLICKSTREAM_EVENT_NAMES.TAP,
            properties: {
              item_name: "FAQ",
              item_location: currentUrl
            }
          };
        }
      }
      break;
    case "access_location_modal":
      clickstreamEventProperties = {
        name: CLICKSTREAM_EVENT_NAMES.ACCESS_LOCATION,
        properties: {}
      };
      break;
    case "location_consent_given":
      {
        const locationData = getStateData(
          reduxStore,
          "api.locationconsentgiven.data",
          null
        );
        clickstreamEventProperties = {
          name: CLICKSTREAM_EVENT_NAMES.ACCESS_LOCATION_CONSET,
          properties: {
            userLocation: locationData?.location,
            userLatitude: locationData?.latitude,
            userLongitude: locationData?.longitude
          }
        };
      }
      break;
    default:
      clickstreamEventProperties = removeSensitiveData(flattenData(data));
      break;
  }
  return {
    timestamp: new Date().getTime(),
    event: {
      ...clickstreamEventProperties
    }
  };
};
